import React from 'react';
import PropTypes from 'prop-types';
import {
  convertFromRaw,
  convertToRaw,
  Editor,
  EditorState,
  getDefaultKeyBinding,
  RichUtils
} from 'draft-js';
import UndoIcon from '@material-ui/icons/Undo';
import RedoIcon from '@material-ui/icons/Redo';
import './RichEditor.scss';
import 'draft-js/dist/Draft.css';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/styles';

const useStyles = {
  editorContentReadOnly: {
    border: 'none'
  },
  contentDescription: {
    fontSize: '1.1rem',
    lineHeight: '1.4em',
    fontFamily: '"Neufile Grotesk Light", sans-serif',
    color: '#212529'
  }
};

class RichEditor extends React.Component {
  constructor(props) {
    super(props);
    const contentText = !props.value
      ? EditorState.createEmpty()
      : EditorState.createWithContent(convertFromRaw(props.value));
    this.state = {
      editorState: contentText
    };
    this.focus = () => this.refs.editor.focus();
    this.onChange = (editorState) => {
      this.setState({ editorState });
      if (!props.readOnly) {
        const contentState = editorState.getCurrentContent();
        const rawContent = convertToRaw(contentState);
        props.onChange(rawContent);
      }
    };
    this.handleKeyCommand = this._handleKeyCommand.bind(this);
    this.mapKeyToEditorCommand = this._mapKeyToEditorCommand.bind(this);
    this.toggleBlockType = this._toggleBlockType.bind(this);
    this.toggleInlineStyle = this._toggleInlineStyle.bind(this);
  }

  _handleKeyCommand(command, editorState) {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      this.onChange(newState);
      return true;
    }
    return false;
  }
  _mapKeyToEditorCommand(e) {
    if (e.keyCode === 9 /* TAB */) {
      const newEditorState = RichUtils.onTab(
        e,
        this.state.editorState,
        4 /* maxDepth */
      );
      if (newEditorState !== this.state.editorState) {
        this.onChange(newEditorState);
      }
      return;
    }
    return getDefaultKeyBinding(e);
  }

  _toggleBlockType(blockType) {
    this.onChange(RichUtils.toggleBlockType(this.state.editorState, blockType));
  }

  _toggleInlineStyle(inlineStyle) {
    this.onChange(
      RichUtils.toggleInlineStyle(this.state.editorState, inlineStyle)
    );
  }
  _onUndoClick = () => {
    this.onChange(EditorState.undo(this.state.editorState));
  };
  _onRedoClick = () => {
    this.onChange(EditorState.redo(this.state.editorState));
  };
  render() {
    const { editorState } = this.state;
    const { readOnly, classes } = this.props;
    let className = 'RichEditor-editor';
    var contentState = editorState.getCurrentContent();
    if (!contentState.hasText()) {
      if (contentState.getBlockMap().first().getType() !== 'unstyled') {
        className += ' RichEditor-hidePlaceholder';
      }
    }
    return (
      <div data-test="details-editor-form" className="RichEditor-root">
        {!readOnly && (
          <BlockStyleControls
            editorState={editorState}
            onToggle={this.toggleBlockType}
          />
        )}
        {!readOnly && (
          <InlineStyleControls
            editorState={editorState}
            onRedoClick={this._onRedoClick}
            onToggle={this.toggleInlineStyle}
            onUndoClick={this._onUndoClick}
          />
        )}
        <div
          className={
            !readOnly
              ? className
              : className + ' ' + classes.editorContentReadOnly
          }
          onClick={this.focus}>
          <Editor
            data-test="details-editor-company-form"
            blockStyleFn={getBlockStyle}
            customStyleMap={styleMap}
            editorState={editorState}
            handleKeyCommand={this.handleKeyCommand}
            keyBindingFn={this.mapKeyToEditorCommand}
            onChange={this.onChange}
            placeholder={!readOnly ? this.props.placeholder : ''}
            readOnly={readOnly}
            ref="editor"
            spellCheck
          />
        </div>
      </div>
    );
  }
}
// Custom overrides for "code" style.
const styleMap = {
  CODE: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
    fontSize: 16,
    padding: 2
  }
};
const getBlockStyle = (block) => {
  switch (block.getType()) {
    case 'blockquote':
      return 'RichEditor-blockquote';
    default:
      return null;
  }
};

class StyleButton extends React.Component {
  constructor() {
    super();
    this.onToggle = (e) => {
      e.preventDefault();
      this.props.onToggle(this.props.style);
    };
  }
  render() {
    let className = 'RichEditor-styleButton';
    if (this.props.active) {
      className += ' RichEditor-activeButton';
    }
    return (
      <span className={className} onMouseDown={this.onToggle}>
        {this.props.label}
      </span>
    );
  }
}
const BLOCK_TYPES = [
  { label: 'Title 1', style: 'header-two' },
  { label: 'Title 2', style: 'header-three' },
  { label: 'UL', style: 'unordered-list-item' },
  { label: 'OL', style: 'ordered-list-item' }
];
const BlockStyleControls = (props) => {
  const { editorState } = props;
  const selection = editorState.getSelection();
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType();
  return (
    <div className="RichEditor-controls">
      {BLOCK_TYPES.map((type) => (
        <StyleButton
          active={type.style === blockType}
          key={type.label}
          label={type.label}
          onToggle={props.onToggle}
          style={type.style}
        />
      ))}
    </div>
  );
};
const INLINE_STYLES = [
  { label: 'Bold', style: 'BOLD' },
  { label: 'Italic', style: 'ITALIC' },
  { label: 'Strikethrough', style: 'STRIKETHROUGH' }
];
const InlineStyleControls = (props) => {
  const { editorState, onUndoClick, onRedoClick } = props;
  const currentStyle = editorState.getCurrentInlineStyle();
  return (
    <div className="RichEditor-controls">
      {INLINE_STYLES.map((type) => (
        <StyleButton
          active={currentStyle.has(type.style)}
          key={type.label}
          label={type.label}
          onToggle={props.onToggle}
          style={type.style}
        />
      ))}
      <IconButton aria-label="delete" onClick={onUndoClick}>
        <UndoIcon />
      </IconButton>
      <IconButton aria-label="delete" onClick={onRedoClick}>
        <RedoIcon />
      </IconButton>
    </div>
  );
};
InlineStyleControls.propTypes = {
  onRedoClick: PropTypes.func,
  onUndoClick: PropTypes.func
};

RichEditor.propTypes = {
  classes: PropTypes.object,
  value: PropTypes.object,
  onChange: PropTypes.func,
  onUndoClick: PropTypes.func,
  onRedoClick: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool
};
export default withStyles(useStyles)(RichEditor);
